/**
 * TaxFilingIdentificationHelper — Checklist of forms and due dates per tax workstream.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSettlementSession } from '../../hooks/useSettlementSession';

export type TaxHelperVariant = 'final-individual' | 'estate-1041' | '706' | 'state' | 'calendar';

interface FormRow {
  id: string;
  formId: string;
  description: string;
  /** Whether this filing applies to this estate */
  applies: boolean;
  dueDate?: string;
  notes?: string;
}

const PRESETS: Record<TaxHelperVariant, FormRow[]> = {
  'final-individual': [
    { id: 'f1', formId: '1040', description: 'Final decedent income tax return', applies: true },
    { id: 'f2', formId: '1310', description: 'Statement for refund due deceased', applies: false },
    { id: 'f3', formId: 'Sch A–F', description: 'Schedules per last-year profile', applies: false },
    { id: 'f4', formId: 'State 540 / IT-201 / etc.', description: 'Resident state final return', applies: true },
  ],
  'estate-1041': [
    { id: 'e1', formId: '1041', description: 'Fiduciary income tax return', applies: true },
    { id: 'e2', formId: 'Sch K-1', description: 'Beneficiary allocations', applies: true },
    { id: 'e3', formId: '56', description: 'Notice concerning fiduciary relationship', applies: false },
    { id: 'e4', formId: 'State fiduciary', description: 'State trust / estate income', applies: false },
  ],
  '706': [
    { id: 's1', formId: '706', description: 'United States estate tax return', applies: false },
    { id: 's2', formId: '706 Sch A–U', description: 'Schedules per asset classes', applies: false },
    { id: 's3', formId: '709', description: 'GST allocation if applicable', applies: false },
    { id: 's4', formId: '8971', description: 'Successor in interest information', applies: false },
  ],
  state: [
    { id: 'st1', formId: 'State estate / inheritance', description: 'Per domicile and situs rules', applies: false },
    { id: 'st2', formId: 'State fiduciary income', description: 'Nexus-based filings', applies: false },
    { id: 'st3', formId: 'Withholding (GIT/REWH)', description: 'Non-resident real estate', applies: false },
    { id: 'st4', formId: 'Clearance certificate', description: 'Release for distribution', applies: false },
  ],
  calendar: [
    { id: 'c1', formId: '4868 / 7004', description: 'Extensions (individual / business / 706)', applies: false },
    { id: 'c2', formId: '1040-ES / 1041-ES', description: 'Estimated vouchers if needed', applies: false },
    { id: 'c3', formId: '706 portability', description: 'DSUE election deadline', applies: false },
    { id: 'c4', formId: 'Local / property', description: 'County or city wraps', applies: false },
  ],
};

interface TaxFilingIdentificationHelperProps {
  estateId: string;
  variant: TaxHelperVariant;
  title: string;
}

export default function TaxFilingIdentificationHelper({ estateId, variant, title }: TaxFilingIdentificationHelperProps) {
  const storageFrameId = `tax-filing-helper-${variant}`;
  const { loadFrameData, saveFrameData, isLoading } = useSettlementSession(estateId);
  const [rows, setRows] = useState<FormRow[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await loadFrameData(storageFrameId);
      const data = res?.data as { rows?: FormRow[] } | undefined;
      if (!cancelled && data?.rows?.length) {
        setRows(
          data.rows.map((r) => {
            const legacy = r as FormRow & { required?: boolean };
            const applies = typeof legacy.applies === 'boolean' ? legacy.applies : !!legacy.required;
            return { ...legacy, applies };
          })
        );
      } else if (!cancelled) {
        setRows(PRESETS[variant]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadFrameData, storageFrameId, variant]);

  const persist = useCallback(
    async (next: FormRow[]) => {
      setRows(next);
      const ok = await saveFrameData(storageFrameId, { rows: next });
      setSaved(ok);
      setTimeout(() => setSaved(false), 2000);
    },
    [saveFrameData, storageFrameId]
  );

  const toggleApplies = (id: string) => {
    void persist(rows.map((r) => (r.id === id ? { ...r, applies: !r.applies } : r)));
  };

  const updateField = (id: string, key: 'dueDate' | 'notes', value: string) => {
    void persist(rows.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-blue-900 mb-1">{title}</h2>
      <p className="text-sm text-stone-600 mb-4">
        Mark which filings apply, capture due dates, and sync with your tax calendar step.
      </p>

      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.id} className="rounded-lg border border-stone-100 bg-stone-50/80 p-3 flex flex-wrap gap-3 items-start">
            <label className="flex items-center gap-2 min-w-[120px]">
              <input
                type="checkbox"
                checked={row.applies}
                onChange={() => toggleApplies(row.id)}
                className="rounded border-stone-300"
              />
              <span className="text-sm font-medium text-stone-900">{row.formId}</span>
            </label>
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm text-stone-700">{row.description}</p>
            </div>
            <input
              type="date"
              value={row.dueDate ?? ''}
              onChange={(e) => updateField(row.id, 'dueDate', e.target.value)}
              className="rounded-md border border-stone-200 px-2 py-1 text-sm"
            />
            <input
              value={row.notes ?? ''}
              onChange={(e) => updateField(row.id, 'notes', e.target.value)}
              placeholder="Notes"
              className="flex-1 min-w-[160px] rounded-md border border-stone-200 px-2 py-1 text-sm"
            />
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        {saved && <span className="text-xs text-green-700">Saved.</span>}
        {isLoading && <span className="text-xs text-stone-500">Syncing…</span>}
      </div>
    </div>
  );
}
